/**
 * autocomplete.ts — Tab completion for command names + subcommands + values.
 *
 * Phase CG (initial) · Phase CI (extended).
 * Bound to the input's keydown; produces a single completion or a list of
 * candidates printed inline. Now covers:
 *   - top-level command names
 *   - subcommands per command (verb-noun shape)
 *   - argument values for `api show`, `protocols show`, `families show`,
 *     `skill install`, `theme`, `docs`, common operator IDs for `compute`
 *     and `operators show`.
 *
 * Slash-prefix handling: a leading `/` is stripped before matching so the
 * slash menu (web-cli.ts) and bare typing share one matcher.
 */

import { commandNames, GROUPS } from "./help.js";
import {
  API_GROUPS, PROTOCOL_THEMES, OPERATOR_FAMILIES,
  KERNEL_INSTALLERS, DOC_MAP,
} from "./sdk-surface.js";

/** Subcommands keyed by command head. Each list = the second token. */
const SUBCOMMANDS: Record<string, string[]> = {
  operators: ["list", "search", "show", "count", "categories", "promote"],
  contract:  ["list", "show", "fire-now", "pause", "resume", "dry-run", "preview", "next-fires", "export", "import", "audit", "events", "deploy"],
  state:     ["head", "audit", "public", "explore", "verify", "export", "stats"],
  agents:    ["spawn", "tick", "kill", "mesh", "list"],
  skills:    ["list", "synthesise", "synthesize", "run"],
  pulse:     ["clock", "status"],
  credits:   ["swap", "tally"],
  keys:      ["list", "mint", "revoke"],
  machine:   ["mint", "genesis"],
  oracle:    ["history"],
  apis:      ["add", "test", "remove", "list"],
  site:      ["list", "deploy", "get", "unpublish"],
  prd:       ["show", "edit", "push"],
  tutorial:  ["next", "back", "exit"],
  zone:      ["write", "clear"],
  help:      [],
  hello:     ["universe"],
  // Phase CI additions
  api:       ["groups", "list", "show", "search", "total", "count"],
  protocols: ["themes", "domains", "show", "search", "list"],
  families:  ["list", "show"],
  skill:     ["info", "endpoints", "prompt", "md", "manifest", "install", "verify"],
  docs:      [], // value completion handles topics
  theme:     ["cryptopunk", "paper", "mono"],
};

/** Common operator IDs surfaced for `compute <Tab>` and `operators show <Tab>`. */
const COMMON_OPERATOR_IDS: string[] = [
  // Newtonian — most-used first
  "NM19", "NM21", "NM22", "NM23", "NM24", "NM25", "NM26", "NM27", "NM30",
  // Quantum
  "QM1", "QM2", "QM5", "QM9", "QM10", "QM11", "QM17",
  // GR
  "GR31", "GR32", "GR33", "GR34", "GR37", "GR41",
  // CS
  "CS43", "CS44", "CS46", "CS47", "CS87",
  // Awareness + carriers
  "KO42", "ON0", "QL1", "TM1", "VX", "ZEQ00",
];

/** Value-completion table: command + arg-index → list of candidates. */
function valuesFor(parts: string[]): string[] {
  const head = parts[0]?.toLowerCase();
  const sub  = parts[1]?.toLowerCase();
  const idx  = parts.length - 1;

  // `compute <Tab>` → operator IDs
  if (head === "compute" && idx === 1) return COMMON_OPERATOR_IDS;
  // `operators show <Tab>` → operator IDs
  if (head === "operators" && sub === "show" && idx === 2) return COMMON_OPERATOR_IDS;
  // `operators list <Tab>` → top domains
  if (head === "operators" && sub === "list" && idx === 2) {
    return ["Newtonian Mechanics", "Quantum Mechanics", "General Relativity", "Computer Science", "Awareness", "Hulyas Forensics", "Photonics", "Acoustics"];
  }
  // `api show <Tab>` → 31 group slugs
  if (head === "api" && (sub === "show" || sub === "open") && idx === 2) return API_GROUPS.map(g => g.slug);
  // `api search <Tab>` → top group names
  if (head === "api" && sub === "search" && idx === 2) return API_GROUPS.slice(0, 8).map(g => g.slug);
  // `protocols show <Tab>` → numbers + theme slugs
  if (head === "protocols" && (sub === "show" || sub === "open") && idx === 2) {
    return PROTOCOL_THEMES.flatMap(t => [String(t.index), t.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")]);
  }
  // `families show <Tab>` → family slugs
  if (head === "families" && (sub === "show" || sub === "open" || sub === "list") && idx === 2) {
    return OPERATOR_FAMILIES.map(f => f.slug);
  }
  // `skill install <Tab>` → client names
  if (head === "skill" && sub === "install" && idx === 2) return Object.keys(KERNEL_INSTALLERS);
  // `theme <Tab>` → 3 themes
  if (head === "theme" && idx === 1) return ["cryptopunk", "paper", "mono"];
  // `docs <Tab>` → all known doc topics
  if (head === "docs" && idx === 1) return Object.keys(DOC_MAP).sort();
  // `help <Tab>` → all command names
  if (head === "help" && idx === 1) return commandNames();

  return [];
}

export function suggest(input: string): { completion: string | null; candidates: string[] } {
  // Strip leading `/` so users can do `/help` ↔ `help` interchangeably
  // and the slash-menu shares the same matcher.
  let raw = input.startsWith("/") ? input.slice(1) : input;
  const t = raw;
  const parts = t.split(/\s+/);

  // ── Top-level command name ────────────────────────────────────────────
  if (parts.length === 1) {
    const stem = parts[0].toLowerCase();
    if (!stem) {
      // Empty input → show the top commands as discoverability hint
      return { completion: null, candidates: commandNames().slice(0, 12) };
    }
    const all = commandNames();
    const matches = all.filter(c => c.toLowerCase().startsWith(stem));
    if (matches.length === 1) return { completion: matches[0] + " ", candidates: [] };
    if (matches.length > 1) {
      const lcp = longestCommonPrefix(matches);
      return { completion: lcp.length > stem.length ? lcp : null, candidates: matches };
    }
    return { completion: null, candidates: [] };
  }

  // ── Subcommand or argument-value completion ───────────────────────────
  const head = parts[0].toLowerCase();
  const last = parts[parts.length - 1].toLowerCase();

  // First try subcommand list (if at index 1)
  if (parts.length === 2) {
    const subs = SUBCOMMANDS[head];
    if (subs && subs.length > 0) {
      if (!last) return { completion: null, candidates: subs };
      const matches = subs.filter(s => s.toLowerCase().startsWith(last));
      if (matches.length === 1) {
        parts[parts.length - 1] = matches[0];
        return { completion: parts.join(" ") + " ", candidates: [] };
      }
      if (matches.length > 1) {
        const lcp = longestCommonPrefix(matches);
        if (lcp.length > last.length) {
          parts[parts.length - 1] = lcp;
          return { completion: parts.join(" "), candidates: matches };
        }
        return { completion: null, candidates: matches };
      }
    }
  }

  // Then try value-completion for the current token
  const values = valuesFor(parts);
  if (values.length) {
    if (!last) return { completion: null, candidates: values.slice(0, 20) };
    const matches = values.filter(v => v.toLowerCase().startsWith(last));
    if (matches.length === 1) {
      parts[parts.length - 1] = matches[0];
      return { completion: parts.join(" ") + " ", candidates: [] };
    }
    if (matches.length > 1) {
      const lcp = longestCommonPrefix(matches);
      if (lcp.length > last.length) {
        parts[parts.length - 1] = lcp;
        return { completion: parts.join(" "), candidates: matches };
      }
      return { completion: null, candidates: matches.slice(0, 20) };
    }
  }

  return { completion: null, candidates: [] };
}

function longestCommonPrefix(strs: string[]): string {
  if (!strs.length) return "";
  let p = strs[0];
  for (let i = 1; i < strs.length; i++) {
    while (strs[i].indexOf(p) !== 0) {
      p = p.slice(0, -1);
      if (!p) return "";
    }
  }
  return p;
}

// ──────────────────────────────────────────────────────────────────────────
// Phase CI.3 — Slash-menu support.
//   • slashCatalogue() returns one entry per top-level command, dedup'd by
//     head, decorated with its category + one-line description + first
//     runnable example. Built lazily and memoised since GROUPS is static.
//   • slashFilter(q) returns the filtered catalogue ranked by:
//       1. command-name prefix match
//       2. command-name substring
//       3. description substring
//     The empty query returns the first 30 entries (every distinct head).
//   The web-cli.ts overlay UI consumes both functions; no DOM access here.
// ──────────────────────────────────────────────────────────────────────────

export interface SlashEntry {
  name: string;        // top-level command (no slash, no args)
  category: string;    // help.ts category label (lowercase)
  blurb: string;       // one-line description from help GROUPS
  example?: string;    // first runnable example
  args?: string;       // argument hint (e.g. "<op> [k=v ...]") for first variant
}

let _slashCache: SlashEntry[] | null = null;
export function slashCatalogue(): SlashEntry[] {
  if (_slashCache) return _slashCache;
  const seen = new Set<string>();
  const out: SlashEntry[] = [];
  for (const g of GROUPS) {
    for (const c of g.cmds) {
      const head = c.name.split(/\s+/)[0];
      if (seen.has(head)) continue;
      seen.add(head);
      out.push({
        name: head,
        category: g.category,
        blurb: c.desc,
        example: c.example,
        args: c.args,
      });
    }
  }
  _slashCache = out;
  return out;
}

/** Filter the slash catalogue by free-text query. Ranks by prefix-match then substring. */
export function slashFilter(query: string): SlashEntry[] {
  const q = query.replace(/^\//, "").trim().toLowerCase();
  const cat = slashCatalogue();
  if (!q) return cat.slice(0, 30);
  const starts: SlashEntry[] = [];
  const nameSubstr: SlashEntry[] = [];
  const descSubstr: SlashEntry[] = [];
  for (const e of cat) {
    const n = e.name.toLowerCase();
    if (n.startsWith(q)) starts.push(e);
    else if (n.includes(q)) nameSubstr.push(e);
    else if (e.blurb.toLowerCase().includes(q)) descSubstr.push(e);
  }
  return [...starts, ...nameSubstr, ...descSubstr].slice(0, 30);
}
